import useSWR from 'swr'
import { ThrowableApiError } from './errors'
import type { ThrowableApiErrorData } from './errors'
import type { ErrorCode, ErrorData, StringHeaders } from '@captaincause/types'
import type {
  BareFetcher,
  PublicConfiguration,
  SWRResponse,
} from 'swr/_internal'

type FetcherArgs = [
  string,
  StringHeaders | undefined,
  RequestCredentials | undefined,
]

export async function fetcher<Data extends Object>([
  url,
  headers,
  credentials,
]: FetcherArgs): Promise<Data> {
  const requestHeader = new Headers(headers)
  requestHeader.set('Content-Type', 'application/json')
  const [response] = await Promise.all([
    fetch(url, {
      method: 'GET',
      headers: requestHeader,
      credentials,
    }),
  ])

  if (!response.ok) {
    const erroredJson: ErrorData = await response.json()

    throw ThrowableApiError({
      message: 'useFetch: error while fetching',
      status: response.status,
      data: {
        error: erroredJson?.error || '',
        code:
          erroredJson?.code ||
          (response.status.toString() as ErrorCode) ||
          '400',
      },
    })
  }

  return (await response.json()) as Data
}

export interface UseFetchArgs<Data> {
  url: string
  skipFetch?: boolean
  headers?: StringHeaders
  config?: Partial<PublicConfiguration<Data, any, BareFetcher<Data>>>
  credentials?: RequestCredentials
}

export function useFetch<Data extends Object>({
  url,
  skipFetch = false,
  headers = undefined,
  credentials = undefined,
  config = undefined,
}: UseFetchArgs<Data>): SWRResponse<Data, ThrowableApiErrorData> {
  return useSWR<Data>(
    skipFetch ? null : [url, headers, credentials],
    fetcher,
    config,
  )
}
